import { MediaLoader, mediaLoaderKind } from './MediaLoader.js';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Section } from '@volt/shared';
import {
  getImageModelSpec,
  validateImageFiles,
  IMAGE_FORMAT_TO_MIME,
  IMAGE_MODEL_IDS,
} from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { useSession } from '../state/session.js';
import { useUI } from '../state/ui.js';
import { useJobs } from '../hooks/useJobs.js';
import { useInfiniteScrollSentinel } from '../hooks/useInfiniteScrollSentinel.js';
import { prefs } from '../lib/storage.js';
import { extractErrorCode } from '../lib/dom.js';
import { downloadUrl, getExtensionFromUrl, jobPreviewUrl } from '../lib/download.js';
import { buildJobTiles } from '../lib/jobTiles.js';
import { lockBodyScroll } from '../lib/bodyScrollLock.js';
import { useGeneratedLibrary } from '../hooks/useGeneratedLibrary.js';
import { ModelPicker } from './ModelPicker.js';
import { LibraryPickerModal } from './LibraryPickerModal.js';
import { Icon } from './Icon.js';
import { JobActionsMenu } from './JobActionsMenu.js';
import { ImageLightbox } from './MediaLightbox.js';
import { VideoJobScreen } from './VideoJobScreen.js';
import { Video2JobScreen } from './Video2JobScreen.js';
import type { Job } from '@volt/shared';
import type { TranslationKey } from '../i18n/index.js';

const MODEL_PREF_KEYS: Record<Section, string> = {
  chat: 'lastModelChat',
  image: 'lastModelImage',
  video: 'lastModelVideo',
  video2: 'lastModelVideo2',
  character: 'lastModelCharacter',
  audio: 'lastModelAudio',
  voice: 'lastModelVoice',
  design: 'lastModelDesign',
};

const PLACEHOLDER_KEYS: Partial<Record<Section, TranslationKey>> = {
  image: 'composer.imagePlaceholder',
  video: 'composer.videoPlaceholder',
  video2: 'composer.videoPlaceholder',
  character: 'composer.characterPlaceholder',
  audio: 'composer.audioPlaceholder',
  voice: 'composer.voicePlaceholder',
};

const STATUS_KEYS: Record<string, TranslationKey> = {
  pending: 'job.pending',
  processing: 'job.processing',
  done: 'job.done',
  failed: 'job.failed',
  cancelled: 'job.cancelled',
};

interface JobItemProps {
  job: Job;
}

function JobItem({ job }: JobItemProps) {
  const { t } = useI18n();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const status = STATUS_KEYS[job.status] ?? 'job.pending';
  const prompt = (job.input as { prompt?: string }).prompt ?? '';
  const section = (job.input as { section?: string }).section ?? '';
  const isImageJob = section === 'image';
  const resultUrls =
    Array.isArray(job.resultUrls) && job.resultUrls.length > 0
      ? job.resultUrls
      : job.resultUrl
        ? [job.resultUrl]
        : [];

  return (
    <div className={`job-item job-${job.status}`}>
      <div className="job-header">
        <span className="job-model">{job.modelId}</span>
        <span className="job-status">
          {(job.status === 'pending' || job.status === 'processing') && <MediaLoader kind={mediaLoaderKind(section)} size={22} />}
          {t(status)}
        </span>
      </div>
      <p className="job-prompt">{prompt}</p>
      {isImageJob && resultUrls.length > 0 ? (
        <div className="job-image-grid">
          {resultUrls.map((url, index) => (
            <div key={`${job.id}-img-${index}`} className="job-image-wrap">
              <button
                type="button"
                className="job-image-btn"
                onClick={() => setLightboxIndex(index)}
                aria-label={`View image ${index + 1}`}
              >
                <img
                  className="job-image-thumb"
                  src={jobPreviewUrl(job, index, url)}
                  alt={`Result ${index + 1}`}
                  loading="lazy"
                />
              </button>
              <button
                type="button"
                className="btn btn-ghost job-image-dl"
                onClick={() =>
                  downloadUrl(url, `image-${index + 1}.${getExtensionFromUrl(url, 'image')}`)
                }
                aria-label={`Download image ${index + 1}`}
              >
                <Icon name="download" size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        resultUrls.map((url, index) => (
          <a
            key={`${job.id}-${url}-${index}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost job-result"
          >
            <Icon name="download" size={16} />
            {resultUrls.length > 1 ? `${t('job.result')} #${index + 1}` : t('job.result')}
          </a>
        ))
      )}
      {job.error && <p className="job-error">{job.error}</p>}
      {lightboxIndex !== null && (
        <ImageLightbox
          urls={resultUrls}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

interface JobGridTileProps {
  job: Job;
  resultUrl: string | null;
  resultIndex: number;
  allUrls: string[];
  onOpenLightbox: (urls: string[], index: number) => void;
  onToggleFavorite: (job: Job, isFavorite: boolean) => void;
  onRegenerate: (job: Job) => void;
  onDelete: (job: Job) => void;
}

function JobGridTile({
  job,
  resultUrl,
  resultIndex,
  allUrls,
  onOpenLightbox,
  onToggleFavorite,
  onRegenerate,
  onDelete,
}: JobGridTileProps) {
  const { t } = useI18n();
  const status = STATUS_KEYS[job.status] ?? 'job.pending';
  const isErrored = job.status === 'failed' || job.status === 'cancelled';
  const showOverlay = !resultUrl || isErrored;
  const filename = resultUrl
    ? `image-${resultIndex + 1}.${getExtensionFromUrl(resultUrl, 'image')}`
    : '';

  return (
    <div className={`job-tile job-tile-${job.status}`}>
      {resultUrl ? (
        <button
          type="button"
          className="job-tile-preview"
          onClick={() => onOpenLightbox(allUrls, resultIndex)}
          aria-label={t('job.result')}
        >
          <img
            className="job-tile-img"
            src={jobPreviewUrl(job, resultIndex, resultUrl)}
            alt=""
            loading="lazy"
          />
        </button>
      ) : (
        <div className="job-tile-preview job-tile-placeholder" aria-hidden="true" />
      )}

      {showOverlay && (
        <div className={`job-tile-overlay${isErrored ? ' job-tile-overlay-error' : ''}`}>
          {isErrored ? (
            <span className="job-tile-status-text">{job.error || t(status)}</span>
          ) : (
            <>
              <MediaLoader kind={mediaLoaderKind((job.input as { section?: string }).section)} size={36} />
              <span className="job-tile-status-text">{t(status)}</span>
            </>
          )}
        </div>
      )}

      {job.isFavorite && (
        <span className="job-tile-favorite" role="img" aria-label={t('job.favorited')}>
          <Icon name="star" size={14} />
        </span>
      )}

      <JobActionsMenu
        job={job}
        downloadHref={resultUrl}
        downloadFilename={filename || 'image'}
        resultIndex={resultIndex}
        onToggleFavorite={onToggleFavorite}
        onRegenerate={onRegenerate}
        onDelete={onDelete}
      />
    </div>
  );
}

interface ImageJobGridProps {
  jobs: Job[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onRegenerate: (job: Job) => void;
  onToggleFavorite: (job: Job, isFavorite: boolean) => void;
  onDelete: (job: Job) => void;
}

/** 3-column infinite-scroll grid of generated image tiles (used for the "image" section). */
function ImageJobGrid({
  jobs,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onRegenerate,
  onToggleFavorite,
  onDelete,
}: ImageJobGridProps) {
  const { t } = useI18n();
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number } | null>(null);
  const sentinelRef = useInfiniteScrollSentinel<HTMLDivElement>(onLoadMore, {
    enabled: hasMore && !loading,
  });

  if (loading && jobs.length === 0) {
    return (
      <div className="jobs-loading">
        <span className="spinner" />
      </div>
    );
  }
  if (jobs.length === 0) {
    return <p className="jobs-empty">{t('job.empty')}</p>;
  }

  const tiles = buildJobTiles(jobs);

  return (
    <>
      <div className="job-grid">
        {tiles.map((tile) => (
          <JobGridTile
            key={tile.key}
            job={tile.job}
            resultUrl={tile.resultUrl}
            resultIndex={tile.resultIndex}
            allUrls={tile.resultUrls}
            onOpenLightbox={(urls, index) => setLightbox({ urls, index })}
            onToggleFavorite={onToggleFavorite}
            onRegenerate={onRegenerate}
            onDelete={onDelete}
          />
        ))}
        {hasMore && <div ref={sentinelRef} className="job-grid-sentinel" aria-hidden="true" />}
      </div>
      {loadingMore && (
        <div className="jobs-loading jobs-loading-more">
          <span className="spinner" />
        </div>
      )}
      {lightbox && (
        <ImageLightbox
          urls={lightbox.urls}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
}

/** Format bytes to human-readable string (e.g. "30 MB"). */
function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

interface MediaJobScreenProps {
  section: Section;
}

export function MediaJobScreen(props: MediaJobScreenProps) {
  if (props.section === 'video') return <VideoJobScreen />;
  if (props.section === 'video2') return <Video2JobScreen />;
  return <StandardMediaJobScreen {...props} />;
}

type ImageOptionKey =
  'nativeOutput' | 'flexibleRatio' | 'outputCount' | 'outputFormat' | 'background' | 'quality';

const IMAGE_OPTION_MENU_IDS: Record<ImageOptionKey, string> = {
  nativeOutput: 'image-option-menu-native-output',
  flexibleRatio: 'image-option-menu-flexible-ratio',
  outputCount: 'image-option-menu-output-count',
  outputFormat: 'image-option-menu-output-format',
  background: 'image-option-menu-background',
  quality: 'image-option-menu-quality',
};

interface ImageOptionMenuPortalProps {
  menuId: string;
  open: boolean;
  anchorEl: HTMLButtonElement | null;
  label: string;
  options: Array<{
    key: string;
    label: string;
    selected: boolean;
    onSelect: () => void;
  }>;
  onClose: () => void;
}

function ImageOptionMenuPortal({
  menuId,
  open,
  anchorEl,
  label,
  options,
  onClose,
}: ImageOptionMenuPortalProps) {
  const menuRef = useRef<HTMLUListElement>(null);
  const [style, setStyle] = useState<{ top: number; left: number; minWidth: number }>({
    top: 0,
    left: 0,
    minWidth: 160,
  });

  useLayoutEffect(() => {
    if (!open || !anchorEl) return;

    const gap = 6;
    const viewportPadding = 8;
    const updatePosition = () => {
      const anchorRect = anchorEl.getBoundingClientRect();
      const menuRect = menuRef.current?.getBoundingClientRect();
      const menuHeight = menuRect?.height ?? 220;
      const menuWidth = Math.max(anchorRect.width, menuRect?.width ?? 160);
      const canOpenUp =
        anchorRect.bottom + gap + menuHeight > window.innerHeight - viewportPadding &&
        anchorRect.top - gap - menuHeight >= viewportPadding;
      const nextTop = canOpenUp
        ? Math.max(viewportPadding, anchorRect.top - menuHeight - gap)
        : Math.min(window.innerHeight - menuHeight - viewportPadding, anchorRect.bottom + gap);
      const desiredLeft = anchorRect.left;
      const nextLeft = Math.min(
        window.innerWidth - menuWidth - viewportPadding,
        Math.max(viewportPadding, desiredLeft),
      );

      setStyle({ top: nextTop, left: nextLeft, minWidth: anchorRect.width });
    };

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target) || anchorEl.contains(target)) return;
      onClose();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        anchorEl.focus();
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [anchorEl, onClose, open]);

  useLayoutEffect(() => {
    if (!open) return;
    const optionNodes = menuRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    const selectedNode = menuRef.current?.querySelector<HTMLElement>(
      '[role="option"][aria-selected="true"]',
    );
    (selectedNode ?? optionNodes?.[0])?.focus();
  }, [open]);

  if (!open || !anchorEl) return null;

  return createPortal(
    <ul
      id={menuId}
      ref={menuRef}
      className="custom-select-menu image-option-menu image-option-menu-portal"
      role="listbox"
      aria-label={label}
      style={{
        top: style.top,
        left: style.left,
        minWidth: style.minWidth,
        right: 'auto',
        width: 'max-content',
      }}
    >
      {options.map((option) => (
        <li
          key={option.key}
          role="option"
          aria-selected={option.selected}
          tabIndex={0}
          className={`custom-select-option${option.selected ? ' is-selected' : ''}`}
          onClick={option.onSelect}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              anchorEl.focus();
              option.onSelect();
              return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              const optionNodes = Array.from(
                menuRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? [],
              );
              const currentIndex = optionNodes.findIndex((node) => node === event.currentTarget);
              if (currentIndex === -1) return;
              const direction = event.key === 'ArrowDown' ? 1 : -1;
              const nextIndex =
                (currentIndex + direction + optionNodes.length) % optionNodes.length;
              optionNodes[nextIndex]?.focus();
            }
          }}
        >
          {option.label}
        </li>
      ))}
    </ul>,
    document.body,
  );
}

const IMAGE_REFERENCE_KINDS = ['image'] as const;

/** Shared screen for async media generation sections (image, audio, voice). */
function StandardMediaJobScreen({ section }: MediaJobScreenProps) {
  const { t } = useI18n();
  const { data } = useSession();
  const { toast } = useUI();
  const [prompt, setPrompt] = useState('');
  const [pickerAnchor, setPickerAnchor] = useState<{ x: number; y: number } | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string | undefined>(() => {
    return prefs.get(MODEL_PREF_KEYS[section] ?? '') ?? undefined;
  });

  // Image-specific generation options
  const [nativeOutput, setNativeOutput] = useState('');
  const [flexibleRatio, setFlexibleRatio] = useState('');
  const [outputCount, setOutputCount] = useState(1);
  const [outputFormat, setOutputFormat] = useState('');
  const [quality, setQuality] = useState<'Low' | 'Medium' | 'High'>('Medium');
  const [background, setBackground] = useState<'opaque' | 'transparent'>('opaque');
  const [openImageOption, setOpenImageOption] = useState<ImageOptionKey | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [attachedFilePreviews, setAttachedFilePreviews] = useState<
    Array<{ file: File; url: string }>
  >([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const library = useGeneratedLibrary(IMAGE_REFERENCE_KINDS, { enabled: section === 'image' });
  const imageOptionTriggerRefs = useRef<Record<ImageOptionKey, HTMLButtonElement | null>>({
    nativeOutput: null,
    flexibleRatio: null,
    outputCount: null,
    outputFormat: null,
    background: null,
    quality: null,
  });

  const {
    jobs,
    loading,
    loadingMore,
    hasMore,
    generating,
    create,
    loadMore,
    toggleFavorite,
    removeJob,
  } = useJobs(true);
  const sectionJobs = jobs.filter((j) => {
    const input = j.input as Record<string, unknown>;
    return input.section === section || j.modelId === selectedModelId;
  });

  const models = data?.models ?? [];
  const sectionModels = models.filter((model) => {
    if (model.section !== section || !model.isActive) return false;
    if (section === 'image' && !IMAGE_MODEL_IDS.has(model.id)) return false;
    return true;
  });
  const pickerModels = section === 'image' ? sectionModels : models;

  // Ensure selected model is valid for this section
  useEffect(() => {
    const availableSectionModels = sectionModels.filter((model) => model.isAvailable);
    if (!selectedModelId || !availableSectionModels.find((m) => m.id === selectedModelId)) {
      const first = availableSectionModels[0];
      if (first) setSelectedModelId(first.id);
    }
  }, [sectionModels, selectedModelId]);

  const selectedModel = sectionModels.find((m) => m.id === selectedModelId);
  const imageSpec =
    section === 'image' && selectedModelId ? getImageModelSpec(selectedModelId) : undefined;

  // When the model changes, reset options to valid defaults for that model
  useEffect(() => {
    if (!imageSpec) return;
    setNativeOutput((prev) =>
      imageSpec.nativeOutputs.includes(prev as never) ? prev : (imageSpec.nativeOutputs[0] ?? ''),
    );
    setFlexibleRatio((prev) =>
      imageSpec.flexibleRatios.includes(prev as never) ? prev : (imageSpec.flexibleRatios[0] ?? ''),
    );
    setOutputCount((prev) => Math.min(prev, imageSpec.maxOutputImages) || 1);
    setOutputFormat((prev) =>
      imageSpec.outputFormats.includes(prev as never) ? prev : (imageSpec.outputFormats[0] ?? ''),
    );
    setQuality((prev) =>
      imageSpec.qualityOptions?.includes(prev) ? prev : (imageSpec.qualityOptions?.[1] ?? 'Medium'),
    );
    setBackground('opaque');
    setAttachedFiles([]);
    setOpenImageOption(null);
    setFileErrors([]);
  }, [selectedModelId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const previews = attachedFiles.map((file) => ({ file, url: URL.createObjectURL(file) }));
    setAttachedFilePreviews(previews);
    return () => {
      for (const preview of previews) URL.revokeObjectURL(preview.url);
    };
  }, [attachedFiles]);

  const handleFilesSelected = useCallback(
    (files: ArrayLike<File> | null) => {
      if (!files || !imageSpec) return;
      const newFiles = Array.from(files);
      const allFiles = [...attachedFiles, ...newFiles];

      const errors: string[] = [];
      const validationInput = allFiles.map((f) => ({ name: f.name, mime: f.type, size: f.size }));
      const validationErrors = validateImageFiles(validationInput, imageSpec);

      for (const err of validationErrors) {
        if (err.type === 'count') {
          errors.push(t('image.validationCount', { max: String(imageSpec.maxInputImages) }));
          break;
        } else if (err.type === 'format') {
          errors.push(t('image.validationFormat', { name: err.fileName, mime: err.mime ?? '' }));
        } else if (err.type === 'size') {
          errors.push(
            t('image.validationSize', { name: err.fileName, max: formatBytes(err.maxBytes ?? 0) }),
          );
        }
      }

      if (errors.length > 0) {
        setFileErrors(errors);
        // Keep only valid files (within count, size, format)
        const allowedMimes = new Set(imageSpec.inputFormats.map((f) => IMAGE_FORMAT_TO_MIME[f]));
        const validFiles = allFiles
          .filter((f) => allowedMimes.has(f.type) && f.size <= imageSpec.imageSizeMaxBytes)
          .slice(0, imageSpec.maxInputImages);
        setAttachedFiles(validFiles);
      } else {
        setFileErrors([]);
        setAttachedFiles(allFiles.slice(0, imageSpec.maxInputImages));
      }
    },
    [attachedFiles, imageSpec, t],
  );

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || !selectedModelId) return;

    const imageOptions =
      section === 'image' && imageSpec
        ? {
            nativeOutput: nativeOutput || undefined,
            ratio: flexibleRatio || undefined,
            outputCount,
            outputFormat: outputFormat || undefined,
            background: imageSpec.supportsBackground ? background : undefined,
            quality: imageSpec.qualityOptions ? quality : undefined,
            inputImageCount: attachedFiles.length > 0 ? attachedFiles.length : undefined,
          }
        : {};

    try {
      await create({
        modelId: selectedModelId,
        prompt: prompt.trim(),
        options: { section, ...imageOptions },
      });
      setPrompt('');
      setAttachedFiles([]);
      setFileErrors([]);
    } catch (error: unknown) {
      const code = extractErrorCode(error);
      toast(t(('error.' + code) as Parameters<typeof t>[0]), 'error');
    }
  }, [
    prompt,
    selectedModelId,
    create,
    section,
    t,
    toast,
    imageSpec,
    nativeOutput,
    flexibleRatio,
    outputCount,
    outputFormat,
    quality,
    background,
    attachedFiles,
  ]);

  const handleRegenerate = useCallback(
    (job: Job) => {
      const { prompt: regenPrompt, ...options } = (job.input ?? {}) as Record<string, unknown> & {
        prompt?: string;
      };
      void create({
        modelId: job.modelId,
        prompt: typeof regenPrompt === 'string' ? regenPrompt : '',
        conversationId: job.conversationId ?? undefined,
        options,
      }).catch((error: unknown) => {
        const code = extractErrorCode(error);
        toast(t(('error.' + code) as Parameters<typeof t>[0]), 'error');
      });
    },
    [create, t, toast],
  );

  const handleToggleFavorite = useCallback(
    (job: Job, isFavorite: boolean) => {
      void toggleFavorite(job.id, isFavorite).catch((error: unknown) => {
        const code = extractErrorCode(error);
        toast(t(('error.' + code) as Parameters<typeof t>[0]), 'error');
      });
    },
    [toggleFavorite, t, toast],
  );

  const handleDeleteJob = useCallback(
    (job: Job) => {
      void removeJob(job.id).catch((error: unknown) => {
        const code = extractErrorCode(error);
        toast(t(('error.' + code) as Parameters<typeof t>[0]), 'error');
      });
    },
    [removeJob, t, toast],
  );

  const placeholder = t(
    (PLACEHOLDER_KEYS[section] ?? 'composer.placeholder') as Parameters<typeof t>[0],
  );

  const isTextOnly = imageSpec ? imageSpec.modalities.every((m) => m === 'Text to Image') : false;
  const acceptTypes = imageSpec
    ? imageSpec.inputFormats.map((f) => IMAGE_FORMAT_TO_MIME[f]).join(',')
    : 'image/*';

  const toggleImageOption = useCallback((option: ImageOptionKey) => {
    setOpenImageOption((prev) => (prev === option ? null : option));
  }, []);

  const closeImageOption = useCallback(() => {
    setOpenImageOption(null);
  }, []);

  // Lock body scroll while any image option dropdown is open
  useEffect(() => {
    if (!openImageOption) return;
    return lockBodyScroll();
  }, [openImageOption]);

  return (
    <div className="media-screen">
      <div className="media-composer">
        <div className="composer-model-row">
          <button
            type="button"
            className="composer-model-btn"
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              setPickerAnchor({ x: rect.left, y: rect.bottom + 8 });
            }}
          >
            <Icon name="settings" size={14} />
            <span>{selectedModel?.displayName ?? t('composer.selectModel')}</span>
            <Icon name="chevronDown" size={14} />
          </button>
        </div>

        {/* Image generation selectors */}
        {section === 'image' && imageSpec && (
          <div className="image-options-row" data-no-swipe>
            <div
              className={`custom-select image-option-select image-option-select--primary${openImageOption === 'nativeOutput' ? ' is-open' : ''}`}
            >
              <button
                type="button"
                className="custom-select-trigger image-option-trigger"
                ref={(node) => {
                  imageOptionTriggerRefs.current.nativeOutput = node;
                }}
                onClick={() => toggleImageOption('nativeOutput')}
                aria-label={t('image.nativeOutput')}
                aria-haspopup="listbox"
                aria-expanded={openImageOption === 'nativeOutput'}
                aria-controls={IMAGE_OPTION_MENU_IDS.nativeOutput}
              >
                <span>{`${t('image.nativeOutput')}: ${nativeOutput}`}</span>
                <Icon name="chevronDown" size={12} />
              </button>
              <ImageOptionMenuPortal
                menuId={IMAGE_OPTION_MENU_IDS.nativeOutput}
                open={openImageOption === 'nativeOutput'}
                anchorEl={imageOptionTriggerRefs.current.nativeOutput}
                label={t('image.nativeOutput')}
                onClose={closeImageOption}
                options={imageSpec.nativeOutputs.map((value) => ({
                  key: value,
                  label: value,
                  selected: nativeOutput === value,
                  onSelect: () => {
                    setNativeOutput(value);
                    closeImageOption();
                  },
                }))}
              />
            </div>

            <div
              className={`custom-select image-option-select image-option-select--primary${openImageOption === 'flexibleRatio' ? ' is-open' : ''}`}
            >
              <button
                type="button"
                className="custom-select-trigger image-option-trigger"
                ref={(node) => {
                  imageOptionTriggerRefs.current.flexibleRatio = node;
                }}
                onClick={() => toggleImageOption('flexibleRatio')}
                aria-label={t('image.flexibleRatio')}
                aria-haspopup="listbox"
                aria-expanded={openImageOption === 'flexibleRatio'}
                aria-controls={IMAGE_OPTION_MENU_IDS.flexibleRatio}
              >
                <span>{`${t('image.flexibleRatio')}: ${flexibleRatio}`}</span>
                <Icon name="chevronDown" size={12} />
              </button>
              <ImageOptionMenuPortal
                menuId={IMAGE_OPTION_MENU_IDS.flexibleRatio}
                open={openImageOption === 'flexibleRatio'}
                anchorEl={imageOptionTriggerRefs.current.flexibleRatio}
                label={t('image.flexibleRatio')}
                onClose={closeImageOption}
                options={imageSpec.flexibleRatios.map((value) => ({
                  key: value,
                  label: value,
                  selected: flexibleRatio === value,
                  onSelect: () => {
                    setFlexibleRatio(value);
                    closeImageOption();
                  },
                }))}
              />
            </div>

            <div
              className={`custom-select image-option-select image-option-select--primary${openImageOption === 'outputCount' ? ' is-open' : ''}`}
            >
              <button
                type="button"
                className="custom-select-trigger image-option-trigger"
                ref={(node) => {
                  imageOptionTriggerRefs.current.outputCount = node;
                }}
                onClick={() => toggleImageOption('outputCount')}
                aria-label={t('image.outputCount')}
                aria-haspopup="listbox"
                aria-expanded={openImageOption === 'outputCount'}
                aria-controls={IMAGE_OPTION_MENU_IDS.outputCount}
              >
                <span>{`${t('image.outputCount')}: ${outputCount}`}</span>
                <Icon name="chevronDown" size={12} />
              </button>
              <ImageOptionMenuPortal
                menuId={IMAGE_OPTION_MENU_IDS.outputCount}
                open={openImageOption === 'outputCount'}
                anchorEl={imageOptionTriggerRefs.current.outputCount}
                label={t('image.outputCount')}
                onClose={closeImageOption}
                options={Array.from(
                  { length: imageSpec.maxOutputImages },
                  (_, index) => index + 1,
                ).map((value) => ({
                  key: String(value),
                  label: String(value),
                  selected: outputCount === value,
                  onSelect: () => {
                    setOutputCount(value);
                    closeImageOption();
                  },
                }))}
              />
            </div>

            <div
              className={`custom-select image-option-select image-option-select--primary${openImageOption === 'outputFormat' ? ' is-open' : ''}`}
            >
              <button
                type="button"
                className="custom-select-trigger image-option-trigger"
                ref={(node) => {
                  imageOptionTriggerRefs.current.outputFormat = node;
                }}
                onClick={() => toggleImageOption('outputFormat')}
                aria-label={t('image.outputFormat')}
                aria-haspopup="listbox"
                aria-expanded={openImageOption === 'outputFormat'}
                aria-controls={IMAGE_OPTION_MENU_IDS.outputFormat}
              >
                <span>{`${t('image.outputFormat')}: ${outputFormat}`}</span>
                <Icon name="chevronDown" size={12} />
              </button>
              <ImageOptionMenuPortal
                menuId={IMAGE_OPTION_MENU_IDS.outputFormat}
                open={openImageOption === 'outputFormat'}
                anchorEl={imageOptionTriggerRefs.current.outputFormat}
                label={t('image.outputFormat')}
                onClose={closeImageOption}
                options={imageSpec.outputFormats.map((value) => ({
                  key: value,
                  label: value,
                  selected: outputFormat === value,
                  onSelect: () => {
                    setOutputFormat(value);
                    closeImageOption();
                  },
                }))}
              />
            </div>

            {imageSpec.supportsBackground && (
              <div
                className={`custom-select image-option-select${openImageOption === 'background' ? ' is-open' : ''}`}
              >
                <button
                  type="button"
                  className="custom-select-trigger image-option-trigger"
                  ref={(node) => {
                    imageOptionTriggerRefs.current.background = node;
                  }}
                  onClick={() => toggleImageOption('background')}
                  aria-label={t('image.background')}
                  aria-haspopup="listbox"
                  aria-expanded={openImageOption === 'background'}
                  aria-controls={IMAGE_OPTION_MENU_IDS.background}
                >
                  <span>{`${t('image.background')}: ${background === 'opaque' ? t('image.backgroundOpaque') : t('image.backgroundTransparent')}`}</span>
                  <Icon name="chevronDown" size={12} />
                </button>
                <ImageOptionMenuPortal
                  menuId={IMAGE_OPTION_MENU_IDS.background}
                  open={openImageOption === 'background'}
                  anchorEl={imageOptionTriggerRefs.current.background}
                  label={t('image.background')}
                  onClose={closeImageOption}
                  options={(['opaque', 'transparent'] as const).map((value) => ({
                    key: value,
                    label:
                      value === 'opaque'
                        ? t('image.backgroundOpaque')
                        : t('image.backgroundTransparent'),
                    selected: background === value,
                    onSelect: () => {
                      setBackground(value);
                      closeImageOption();
                    },
                  }))}
                />
              </div>
            )}

            {imageSpec.qualityOptions && (
              <div
                className={`custom-select image-option-select${openImageOption === 'quality' ? ' is-open' : ''}`}
              >
                <button
                  type="button"
                  className="custom-select-trigger image-option-trigger"
                  ref={(node) => {
                    imageOptionTriggerRefs.current.quality = node;
                  }}
                  onClick={() => toggleImageOption('quality')}
                  aria-label={t('image.quality')}
                  aria-haspopup="listbox"
                  aria-expanded={openImageOption === 'quality'}
                  aria-controls={IMAGE_OPTION_MENU_IDS.quality}
                >
                  <span>{`${t('image.quality')}: ${quality}`}</span>
                  <Icon name="chevronDown" size={12} />
                </button>
                <ImageOptionMenuPortal
                  menuId={IMAGE_OPTION_MENU_IDS.quality}
                  open={openImageOption === 'quality'}
                  anchorEl={imageOptionTriggerRefs.current.quality}
                  label={t('image.quality')}
                  onClose={closeImageOption}
                  options={imageSpec.qualityOptions.map((value) => ({
                    key: value,
                    label: value,
                    selected: quality === value,
                    onSelect: () => {
                      setQuality(value);
                      closeImageOption();
                    },
                  }))}
                />
              </div>
            )}
          </div>
        )}

        <div className="media-input-row">
          <textarea
            className="composer-input"
            placeholder={placeholder}
            value={prompt}
            rows={3}
            onChange={(event) => setPrompt(event.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary media-generate-btn"
            disabled={!prompt.trim() || !selectedModelId || generating}
            onClick={() => void handleGenerate()}
          >
            <Icon name="send" size={18} />
            {t('composer.generate')}
          </button>
        </div>

        {/* File attachment for image section */}
        {section === 'image' && imageSpec && !isTextOnly && (
          <div className="image-attach-row">
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptTypes}
              multiple={imageSpec.maxInputImages > 1}
              style={{ display: 'none' }}
              onChange={(e) => {
                handleFilesSelected(e.target.files);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="btn btn-ghost image-attach-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon name="attach" size={16} />
              {t('image.attachImages')}
            </button>
            {library.hasItems && (
              <button
                type="button"
                className="btn btn-ghost lib-trigger"
                onClick={() => setLibraryOpen(true)}
              >
                <Icon name="folder" size={16} />
                {t('library.fromLibrary')}
              </button>
            )}
            {attachedFiles.length > 0 && (
              <button
                type="button"
                className="btn btn-ghost image-attach-clear"
                onClick={() => {
                  setAttachedFiles([]);
                  setFileErrors([]);
                }}
              >
                <Icon name="close" size={14} />
              </button>
            )}
          </div>
        )}

        {section === 'image' && imageSpec && (
          <LibraryPickerModal
            open={libraryOpen}
            onClose={() => setLibraryOpen(false)}
            library={library}
            maxSelect={Math.max(1, imageSpec.maxInputImages - attachedFiles.length)}
            onPick={(files) => handleFilesSelected(files)}
          />
        )}

        {section === 'image' && attachedFilePreviews.length > 0 && (
          <div
            className="image-attachments-preview"
            aria-label={t('image.attachedImages', { count: String(attachedFilePreviews.length) })}
          >
            {attachedFilePreviews.map((preview, index) => (
              <figure
                key={`${preview.file.name}-${preview.file.lastModified}-${index}`}
                className="image-attachment-item"
              >
                <img src={preview.url} alt={preview.file.name} className="image-attachment-thumb" />
                <figcaption className="image-attachment-caption">{preview.file.name}</figcaption>
                <button
                  type="button"
                  className="icon-btn image-attachment-remove"
                  aria-label={`${t('common.delete')} ${preview.file.name}`}
                  onClick={() => {
                    setAttachedFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
                    setFileErrors([]);
                  }}
                >
                  <Icon name="close" size={12} />
                </button>
              </figure>
            ))}
          </div>
        )}

        {/* File validation errors */}
        {fileErrors.length > 0 && (
          <div className="image-file-errors">
            {fileErrors.map((err, i) => (
              <p key={i} className="image-file-error">
                {err}
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="jobs-section">
        {section === 'image' ? (
          <ImageJobGrid
            jobs={sectionJobs}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            onLoadMore={loadMore}
            onRegenerate={handleRegenerate}
            onToggleFavorite={handleToggleFavorite}
            onDelete={handleDeleteJob}
          />
        ) : loading && sectionJobs.length === 0 ? (
          <div className="jobs-loading">
            <span className="spinner" />
          </div>
        ) : sectionJobs.length === 0 ? (
          <p className="jobs-empty">{t('job.empty')}</p>
        ) : (
          <div className="job-list">
            {sectionJobs.map((job) => (
              <JobItem key={job.id} job={job} />
            ))}
          </div>
        )}
      </div>

      <ModelPicker
        open={pickerAnchor !== null}
        onClose={() => setPickerAnchor(null)}
        models={pickerModels}
        section={section}
        selectedModelChoice={selectedModelId ?? ''}
        resolvedModelId={selectedModelId}
        anchor={pickerAnchor}
        onSelect={(id) => {
          setSelectedModelId(id);
          prefs.set(MODEL_PREF_KEYS[section] ?? '', id);
        }}
      />
    </div>
  );
}
